import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { groupsApi, communityApi } from '../lib/domain';
import { useAuth } from '../lib/hooks';
import { ApiError } from '../lib/api';
import { UnitsEditor } from '../components/UnitsEditor';

type GroupDetail = Awaited<ReturnType<typeof groupsApi.get>>['group'];

export function GroupDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [group, setGroup] = useState<GroupDetail | null>(null);
  const [invitations, setInvitations] = useState<Array<{ id: string; status: string; createdAt: string; link: string | null }>>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    const [g, inv] = await Promise.all([groupsApi.get(id), groupsApi.listInvitations(id).catch(() => ({ invitations: [] }))]);
    setGroup(g.group);
    setInvitations(inv.invitations);
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const isAdmin = group?.members.find((m) => m.userId === user?.id)?.role === 'ADMIN';

  async function invite() {
    if (!id) return;
    setError(null);
    try {
      await groupsApi.createInvitation(id);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Einladung konnte nicht erstellt werden.');
    }
  }

  async function copyLink(link: string) {
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      prompt('Einladungslink kopieren:', link);
    }
  }

  async function toggleCommunity() {
    if (!id || !group) return;
    await communityApi.setOptIn(`group:${id}`, !group.communityOptIn);
    await load();
  }

  async function removeMember(userId: string) {
    if (!id || !confirm('Mitglied entfernen?')) return;
    await groupsApi.removeMember(id, userId);
    await load();
  }

  async function leave() {
    if (!id || !confirm('Gruppe wirklich verlassen?')) return;
    try {
      await groupsApi.leave(id);
      navigate('/groups');
    } catch (err) {
      alert(err instanceof ApiError ? err.message : 'Verlassen nicht möglich.');
    }
  }

  if (!group) return <p>Lade…</p>;

  return (
    <div className="stack">
      <h1>{group.name}</h1>

      {isAdmin && (
        <div className="card stack">
          <h2>Einladung</h2>
          <button className="primary" onClick={invite}>
            Neuen Einladungslink erzeugen
          </button>
          {error && <p className="error-text">{error}</p>}
          <ul className="list-reset stack">
            {invitations.map((i) => (
              <li key={i.id} className="stack">
                <div className="row between">
                  <span>
                    {i.status} <span className="muted">({new Date(i.createdAt).toLocaleString('de-DE')})</span>
                  </span>
                  {i.status === 'PENDING' && (
                    <span className="row">
                      {i.link && <button onClick={() => copyLink(i.link!)}>Kopieren</button>}
                      <button
                        onClick={async () => {
                          await groupsApi.revokeInvitation(id!, i.id);
                          await load();
                        }}
                      >
                        Widerrufen
                      </button>
                    </span>
                  )}
                </div>
                {i.status === 'PENDING' && i.link && (
                  <p className="muted" style={{ wordBreak: 'break-all' }}>
                    {i.link}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="card row between">
        <span>Community-Freigabe (Gruppe)</span>
        <label className="row">
          <input type="checkbox" checked={group.communityOptIn} onChange={toggleCommunity} disabled={!isAdmin} />
          {group.communityOptIn ? 'an' : 'aus'}
        </label>
      </div>

      <UnitsEditor groupId={id!} canEdit={!!isAdmin} />

      <div className="card stack">
        <h2>Mitglieder</h2>
        <ul className="list-reset stack">
          {group.members.map((m) => (
            <li key={m.userId} className="row between">
              <span>
                {m.username ?? m.email} <span className="badge">{m.role}</span>
              </span>
              {isAdmin && m.userId !== user?.id && (
                <button className="danger" onClick={() => removeMember(m.userId)}>
                  Entfernen
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>

      <button className="danger" onClick={leave}>
        Gruppe verlassen
      </button>
    </div>
  );
}
